import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosConfig';
import ExternalLink from './common/ExternalLink';

const AdminJobBoard = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [publishing, setPublishing] = useState({});
  const [bulkPublishing, setBulkPublishing] = useState(false);
  const [bulkPublishResults, setBulkPublishResults] = useState(null);

  const handleRunScraper = async () => {
    setError('');
    setResults([]);
    setProgress(0);
    setIsRunning(true);
    try {
      const { data } = await axios.post('/api/v1/job-board/start');
      setTaskId(data.task_id);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to start scraper');
      setIsRunning(false);
    }
  };

  const annotate = async (tid) => {
    try {
      const { data } = await axios.get(`/api/v1/job-board/annotate/${tid}`);
      setResults(Array.isArray(data.results) ? data.results : []);
    } catch (e) {
      // non-fatal
    }
  };

  const handlePublish = async (index) => {
    if (!taskId) return;
    setPublishing((p) => ({ ...p, [index]: true }));
    try {
      const { data } = await axios.post(`/api/v1/job-board/publish/${taskId}/${index}`);
      if (data.created) {
        // Mark published locally
        setResults((prev) => prev.map((r, i) => (i === index ? { ...r, _published: true } : r)));
      } else if (data.duplicate) {
        // Mark as duplicate locally so the badge updates
        setResults((prev) => prev.map((r, i) => (i === index ? { ...r, duplicate: true } : r)));
      }
      // If validation errors returned, surface a concise message
      if (Array.isArray(data.errors) && data.errors.length > 0) {
        const msg = data.errors
          .map((e) => `${e.field ? e.field + ': ' : ''}${e.message}`)
          .join(' | ');
        setError(msg);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to publish');
    } finally {
      setPublishing((p) => ({ ...p, [index]: false }));
    }
  };

  const handlePublishAllNonDuplicates = async () => {
    if (!taskId) return;
    setBulkPublishing(true);
    setBulkPublishResults(null);
    setError('');

    try {
      const { data } = await axios.post(`/api/v1/job-board/publish-all-non-duplicates/${taskId}`);
      setBulkPublishResults(data);

      // Update the local results to mark published items
      if (data.details) {
        setResults((prev) =>
          prev.map((r, i) => {
            const detail = data.details.find((d) => d.index === i);
            if (detail && detail.status === 'published') {
              return { ...r, _published: true };
            } else if (detail && detail.status.includes('duplicate')) {
              return { ...r, duplicate: true };
            }
            return r;
          })
        );
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to bulk publish');
    } finally {
      setBulkPublishing(false);
    }
  };

  useEffect(() => {
    if (!taskId || !isRunning) return;
    let timer = null;
    const poll = async () => {
      try {
        const { data } = await axios.get(`/api/v1/job-board/progress/${taskId}`);
        setProgress(typeof data.progress === 'number' ? data.progress : 0);
        setResults(Array.isArray(data.results) ? data.results : []);
        if (data.progress >= 100 || data.progress < 0) {
          setIsRunning(false);
          if (timer) clearInterval(timer);
          // annotate once done
          annotate(taskId);
        }
      } catch (e) {
        setError(e?.response?.data?.detail || 'Failed to fetch progress');
        setIsRunning(false);
        if (timer) clearInterval(timer);
      }
    };
    timer = setInterval(poll, 2000);
    // kick immediately
    poll();
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [taskId, isRunning]);

  return (
    <div className="bg-white border border-gray-200 shadow-md rounded-xl p-6">
      <div className="flex justify_between items-center mb-4">
        <h3 className="text-xl font-bold text-blue-600">Job Board</h3>
        <div className="flex gap-3">
          <button
            onClick={handleRunScraper}
            disabled={isRunning}
            className={`px-4 py-2 rounded-md transition-colors text-white ${isRunning ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {isRunning ? 'Running…' : 'Run Scraper'}
          </button>
          {results.length > 0 && !isRunning && (
            <button
              onClick={handlePublishAllNonDuplicates}
              disabled={bulkPublishing}
              className={`px-4 py-2 rounded-md transition-colors text-white ${bulkPublishing ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
            >
              {bulkPublishing ? 'Publishing…' : 'Publish All Non-Duplicates'}
            </button>
          )}
        </div>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Triggers the backend to fetch the latest listings and returns only Software and AI/ML/Data
        roles for Summer 2027.
      </p>

      {error && <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">{error}</div>}

      {bulkPublishResults && (
        <div className="mb-4 p-4 rounded-md bg-blue-50 border border-blue-200">
          <h4 className="text-sm font-semibold text-blue-800 mb-2">Bulk Publish Results</h4>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div className="text-gray-600">
              <span className="font-medium">Total:</span> {bulkPublishResults.total}
            </div>
            <div className="text-green-600">
              <span className="font-medium">Published:</span> {bulkPublishResults.published}
            </div>
            <div className="text-yellow-600">
              <span className="font-medium">Skipped:</span> {bulkPublishResults.skipped}
            </div>
            <div className="text-red-600">
              <span className="font-medium">Errors:</span> {bulkPublishResults.errors}
            </div>
          </div>
        </div>
      )}

      <div className="mb-4">
        <div className="flex items-center justify_between mb-2">
          <span className="text-sm font-medium text-gray-700">Progress</span>
          <span className="text-sm text-gray-500">{progress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full"
            style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
          />
        </div>
      </div>

      <div className="mb-2 text-sm text-gray-700">Results: {results.length}</div>

      <div className="grid grid-cols-1 gap-4">
        {results.length === 0 ? (
          <div className="px-4 py-6 text-center text-gray-500 text-sm">No results yet</div>
        ) : (
          results.map((job, idx) => (
            <div
              key={`${job.id || job.url || idx}`}
              className="border border-gray-200 rounded-lg bg-white"
            >
              <div className="p-4">
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">{job.title}</h3>
                        <div className="flex items-center flex-wrap gap-x-2 mt-1 text-sm">
                          {job.company_name && (
                            <span className="font-medium text-gray-600">{job.company_name}</span>
                          )}
                          {job.locations && (
                            <>
                              <span className="text-gray-400">•</span>
                              <span className="text-gray-500">
                                {Array.isArray(job.locations)
                                  ? job.locations.join(', ')
                                  : job.locations}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {job.url && (
                          <ExternalLink
                            href={job.url}
                            className="py-2.5 px-3 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700"
                          >
                            Apply
                          </ExternalLink>
                        )}
                        <span
                          className={`text-xs px-2 py-1 rounded-sm ${job.duplicate ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}
                        >
                          {job.duplicate ? 'Possible duplicate' : 'Not a duplicate'}
                        </span>
                        <button
                          onClick={() => handlePublish(idx)}
                          disabled={publishing[idx] || job._published}
                          className={`py-2.5 px-3 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border ${publishing[idx] || job._published ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'}`}
                        >
                          {job._published
                            ? 'Published'
                            : publishing[idx]
                              ? 'Publishing…'
                              : 'Publish'}
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 text-sm text-gray-500">
                      {job.category && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">
                          {job.category}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminJobBoard;
